import { useContext } from "react";

import { LanguageContext } from "../languageContext";
import { LngLat, LngLatLike } from "mapbox-gl";
import { Button } from "../ui/button";
import { cn } from "src/lib/utils";

export default function AccessibleAddressInputs({
  resetRouteSelectionTool,
  startAddress,
  setStartAddress,
  endAddress,
  setEndAddress,
  startLngLat,
  setStartLngLat,
  endLngLat,
  setEndLngLat,
  showResetButton,
}: {
  resetRouteSelectionTool: () => void;
  startAddress: string;
  setStartAddress: (address: string) => void;
  endAddress: string;
  setEndAddress: (address: string) => void;
  startLngLat: LngLatLike | null;
  setStartLngLat: (lngLat: LngLatLike) => void;
  endLngLat: LngLatLike | null;
  setEndLngLat: (lngLat: LngLatLike) => void;
  showResetButton: boolean;
}) {
  const { translate } = useContext(LanguageContext);
  const handleAddressSubmit = (
    address: string,
    setLngLat: (lngLat: LngLatLike) => void
  ) => {
    const debounceTimeout = setTimeout(async () => {
      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
            address
          )}.json?access_token=${
            process.env.REACT_APP_MAPBOX_TOKEN
          }&bbox=-122.4547,37.7347,-122.3885,37.7861`
        );
        const data = await response.json();
        if (data.features && data.features.length > 0) {
          const [lng, lat] = data.features[0].center;
          setLngLat(new LngLat(lng, lat));
        } else {
          alert(translate("enterAddressInStudyArea"));
        }
      } catch (error) {
        alert(translate("addressNotFound"));
      }
    }, 1000);

    return () => clearTimeout(debounceTimeout);
  };

  const resetButton = (
    <Button onClick={() => resetRouteSelectionTool()}>
      {translate("reset")}
    </Button>
  );

  return (
    <details className="max-w-[600px] mb-10">
      <summary className="text-gray-400">
        {translate("startEndAddresses")}
      </summary>
      {showResetButton ? (
        resetButton
      ) : (
        <div className="flex flex-col space-y-4 my-4">
          <div className="flex flex-col gap-2">
            <label htmlFor="start-address" className="font-medium">
              {translate("startAddress")}
            </label>
            <div className="flex gap-2">
              <form
                className="space-x-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleAddressSubmit(startAddress, setStartLngLat);
                }}
              >
                <input
                  disabled={!!startLngLat}
                  id="start-address"
                  type="text"
                  className="border border-gray-300 rounded px-3 py-2 min-w-[220px]"
                  placeholder={translate("enterStartAddress")}
                  onChange={(e) => {
                    const address = e.target.value;
                    if (!address) {
                      return setStartAddress("");
                    }
                    setStartAddress(address);
                  }}
                  value={startAddress}
                />
                <Button
                  className={cn(startLngLat ? "hidden" : "")}
                  type="submit"
                >
                  {translate("setTripStart")}
                </Button>
              </form>
            </div>
          </div>
          {startLngLat && (
            <div className="flex flex-col gap-2">
              <label htmlFor="end-address" className="font-medium">
                {translate("endAddress")}
              </label>
              <div className="space-y-2">
                <form
                  className="flex items-end space-x-1"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleAddressSubmit(endAddress, setEndLngLat);
                  }}
                >
                  <input
                    disabled={!!endLngLat || !startLngLat}
                    id="end-address"
                    type="text"
                    className="border border-gray-300 rounded px-3 py-2 min-w-[220px]"
                    placeholder={translate("enterEndAddress")}
                    onChange={(e) => {
                      const address = e.target.value;
                      if (!address) {
                        return setEndAddress("");
                      }
                      setEndAddress(address);
                    }}
                    value={endAddress}
                  />
                  <Button
                    className={cn(startLngLat ? "" : "hidden")}
                    type="submit"
                  >
                    {translate("setEndPoint")}
                  </Button>
                </form>
                {resetButton}
              </div>
            </div>
          )}
        </div>
      )}
    </details>
  );
}
